const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token
    ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
};

/**
 * Obtener lista de interesados con filtros opcionales.
 */
export async function getInteresados({ search = '', id_campus = '', estado_prospecto = '', page = 1, page_size = 20, no_pagination = false } = {}) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (id_campus) params.append('id_campus', id_campus);
  if (estado_prospecto) params.append('estado_prospecto', estado_prospecto);
  if (no_pagination) {
    params.append('no_pagination', '1');
  } else {
    params.append('page', page);
    params.append('page_size', page_size);
  }
  const res = await fetch(`${API_BASE}/escolar/interesados/?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al obtener interesados');
  return res.json();
}

/**
 * Obtener un interesado por ID.
 */
export async function getInteresado(id) {
  const res = await fetch(`${API_BASE}/escolar/interesados/${id}/`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al obtener interesado');
  return res.json();
}

/**
 * Crear un nuevo interesado.
 */
export async function createInteresado(data) {
  const res = await fetch(`${API_BASE}/escolar/interesados/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    let err = {};
    try {
      err = raw ? JSON.parse(raw) : {};
    } catch (_e) {
      err = {};
    }

    const detalle = err.detail || err.error || err.detalle;
    const fallback = raw || `Error HTTP ${res.status} al crear interesado`;
    throw new Error(detalle || fallback);
  }
  return res.json();
}

/**
 * Actualizar un interesado (PATCH parcial).
 */
export async function patchInteresado(id, data) {
  const res = await fetch(`${API_BASE}/escolar/interesados/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || err.error || JSON.stringify(err) || 'Error al actualizar interesado');
  }
  return res.json();
}

/**
 * Eliminar un interesado.
 */
export async function deleteInteresado(id) {
  const res = await fetch(`${API_BASE}/escolar/interesados/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al eliminar interesado');
  return true;
}

/**
 * Actualizar estado de un interesado + comentario de seguimiento.
 */
export async function actualizarEstadoInteresado(id, estado, comentario = '') {
  const res = await fetch(`${API_BASE}/escolar/interesados/${id}/actualizar-estado/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ estado, comentario }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al actualizar estado');
  }
  return res.json();
}

export async function getSeguimientosInteresado(idInteresado) {
  const params = new URLSearchParams();
  params.append('id_interesado', idInteresado);
  params.append('no_pagination', '1');
  const res = await fetch(`${API_BASE}/escolar/interesados-seguimientos/?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al obtener seguimientos del interesado');
  return res.json();
}

export async function createSeguimientoInteresado(data) {
  const res = await fetch(`${API_BASE}/escolar/interesados-seguimientos/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || err.error || JSON.stringify(err) || 'Error al crear seguimiento');
  }
  return res.json();
}

export async function updateSeguimientoInteresado(idSeguimiento, data) {
  const res = await fetch(`${API_BASE}/escolar/interesados-seguimientos/${idSeguimiento}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || err.error || JSON.stringify(err) || 'Error al actualizar seguimiento');
  }
  return res.json();
}

export async function deleteSeguimientoInteresado(idSeguimiento) {
  const res = await fetch(`${API_BASE}/escolar/interesados-seguimientos/${idSeguimiento}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al eliminar seguimiento');
  return true;
}

/**
 * Actualizar un interesado con todos los campos.
 * Alias de patchInteresado para mayor claridad.
 */
export async function actualizarInteresado(id, data) {
  return patchInteresado(id, data);
}

/**
 * Generar matrícula para interesado aceptado → lo convierte en estudiante.
 */
export async function generarMatricula(interesadoId) {
  const res = await fetch(`${API_BASE}/escolar/interesados/${interesadoId}/generar-matricula/`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al generar matrícula');
  }
  return res.json();
}

/**
 * Generar cargo de registro para interesado que ya tiene matrícula.
 */
export async function generarCargoRegistro(interesadoId) {
  const res = await fetch(`${API_BASE}/escolar/interesados/${interesadoId}/generar-cargo/`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al generar cargo');
  }
  return res.json();
}

/**
 * Obtener interesados con estado ACEPTADO (para pantalla de Finanzas).
 */
export async function getInteresadosAceptados(campusId = '') {
  const params = new URLSearchParams();
  if (campusId) params.append('id_campus', campusId);
  const res = await fetch(`${API_BASE}/escolar/interesados/aceptados/?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al obtener interesados aceptados');
  return res.json();
}

// ─── Documentos de Interesados ───────────────────────────────────────────────

const getAuthHeadersFileUpload = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Obtener documentos de un interesado.
 */
export async function getDocumentosInteresado(interesadoId) {
  const res = await fetch(`${API_BASE}/documental/expedientes-interesados/?id_interesado=${interesadoId}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al obtener documentos');
  return res.json();
}

/**
 * Subir documento PDF para un interesado.
 * @param {number} interesadoId
 * @param {string} tipoDocumento - ACTA_NACIMIENTO | CURP | COMPROBANTE_DOMICILIO | INE | CONSTANCIA_ESTUDIOS
 * @param {File} archivo - Archivo PDF
 */
export async function subirDocumentoInteresado(interesadoId, tipoDocumento, archivo) {
  const formData = new FormData();
  formData.append('id_interesado', interesadoId);
  formData.append('tipo_documento', tipoDocumento);
  formData.append('archivo', archivo);
  formData.append('nombre_archivo', archivo.name);

  const res = await fetch(`${API_BASE}/documental/expedientes-interesados/`, {
    method: 'POST',
    headers: getAuthHeadersFileUpload(),
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al subir documento');
  }
  return res.json();
}

/**
 * Eliminar (soft-delete) un documento de interesado.
 */
export async function eliminarDocumentoInteresado(documentoId) {
  const res = await fetch(`${API_BASE}/documental/expedientes-interesados/${documentoId}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al eliminar documento');
  return true;
}

/**
 * Obtener URL de descarga de un documento.
 */
export function getUrlDescargaDocumento(documentoId) {
  return `${API_BASE}/documental/expedientes-interesados/${documentoId}/descargar/`;
}
